/**
 * shared/hooks/usePermissions.ts
 *
 * Hook de permisos empresarial para CELMAXX POS.
 * ──────────────────────────────────────────────────────────────────
 * ESTADO: Infraestructura BASE.
 * Lee cajero.role desde useAuth() y construye el set de permisos.
 * NO modifica ventas, turnos, caja ni ningún módulo existente.
 * ──────────────────────────────────────────────────────────────────
 *
 * Uso:
 *   const { can, hasRole, role } = usePermissions()
 *   if (can('cerrar_turno')) { ... }
 *   if (hasRole('gerente'))  { ... }
 *
 * El hook es memoizado — no recalcula en cada render.
 * Si cajero no tiene role, se asigna 'cajero' por defecto (más restrictivo).
 */

import { useMemo } from 'react'
import { useAuth } from '@features/auth/hooks/useAuth'
import {
  ROLES,
  ROLE_PERMISSIONS,
  ROLE_LEVEL,
  EMERGENCY_PERMISSIONS,
  type Role,
  type Permission,
  type UserPermissions,
} from '@shared/types/roles.types'
import { logWarn } from '@shared/utils/logger'

// ── Constante por defecto ──────────────────────────────────────────────────

const DEFAULT_ROLE: Role = 'cajero'

// ── Validar que el role del cajero sea un Role conocido ───────────────────

function parseRole(raw: string | null | undefined): Role {
  if (!raw) return DEFAULT_ROLE
  if (ROLES.includes(raw as Role)) return raw as Role
  logWarn('auth', `Role desconocido: "${raw}" — usando "${DEFAULT_ROLE}" por defecto`)
  return DEFAULT_ROLE
}

// ── Hook principal ─────────────────────────────────────────────────────────

export function usePermissions(): UserPermissions & {
  role: Role
  isLoading: boolean
  emergencyMode: boolean
} {
  const { cajero } = useAuth()

  return useMemo(() => {
    const role = parseRole(cajero?.role)
    const userId = cajero?.id ?? ''
    const storeId = cajero?.storeId ?? null
    const isLoading = !cajero?.id

    // Construir set de permisos del rol
    const basePerms = ROLE_PERMISSIONS[role] ?? ROLE_PERMISSIONS[DEFAULT_ROLE]
    const permissions = new Set<Permission>(basePerms)

    // Helpers
    const can = (permission: Permission): boolean => permissions.has(permission)

    const hasRole = (targetRole: Role): boolean => role === targetRole

    const atLeast = (targetRole: Role): boolean => ROLE_LEVEL[role] >= ROLE_LEVEL[targetRole]

    return {
      userId,
      role,
      storeId,
      permissions,
      can,
      hasRole,
      atLeast,
      isLoading,
      emergencyMode: false,
    }
  }, [cajero?.id, cajero?.role, cajero?.storeId])
}

// ── Hook de emergencia ─────────────────────────────────────────────────────
// Usar cuando el sistema de permisos no está disponible.
// Otorga solo permisos mínimos para seguir vendiendo.

export function useEmergencyPermissions(): Pick<UserPermissions, 'can' | 'hasRole' | 'atLeast'> & {
  emergencyMode: true
} {
  logWarn('auth', 'Modo emergencia activo — permisos mínimos')
  const emergencySet = new Set<Permission>(EMERGENCY_PERMISSIONS)

  return {
    emergencyMode: true,
    can: (p: Permission) => emergencySet.has(p),
    hasRole: (_: Role) => false,
    atLeast: (_: Role) => false,
  }
}

// ── Helper standalone ──────────────────────────────────────────────────────
// Para usar fuera de componentes React (ej. en hooks de datos).

export function checkPermission(role: Role | null | undefined, permission: Permission): boolean {
  const r = parseRole(role)
  const perms = ROLE_PERMISSIONS[r] ?? []
  return perms.includes(permission)
}

export function checkRole(userRole: Role | null | undefined, targetRole: Role): boolean {
  const r = parseRole(userRole)
  return r === targetRole
}

export function checkAtLeast(userRole: Role | null | undefined, targetRole: Role): boolean {
  const r = parseRole(userRole)
  return ROLE_LEVEL[r] >= ROLE_LEVEL[targetRole]
}
